// Tax Center → Auctions: winners settle outside On-Screen, so an admin
// records what was paid and when. Only paid wins appear on tax documents.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingState from '../common/LoadingState';
import { listAuctionWinners, markAuctionPaid, clearAuctionPaid, errorText, money, toCents } from '../../services/taxApi';

function Row({ w, onChanged, t }) {
  const [amount, setAmount] = useState(w.paidCents == null ? (w.bidCents / 100).toFixed(2) : (w.paidCents / 100).toFixed(2));
  const [date, setDate] = useState(w.paidOn || '');
  const [err, setErr] = useState('');
  const paid = w.paidCents != null;

  const save = async () => {
    setErr('');
    const c = toCents(amount);
    if (!c || Number.isNaN(c)) { setErr(t('tax.badAmount', 'Enter an amount like 1250.00')); return; }
    if (!date) { setErr(t('tax.auctions.needDate', 'Enter the date paid')); return; }
    try { await markAuctionPaid(w.id, { paidCents: c, paidOn: date }); onChanged(); } catch (e) { setErr(errorText(e, t('tax.saveError', 'Could not save.'))); }
  };
  const clear = async () => {
    try { await clearAuctionPaid(w.id); onChanged(); } catch (e) { setErr(errorText(e, t('tax.saveError', 'Could not save.'))); }
  };

  return (
    <tr>
      <td>{w.lot || '—'}<br /><span className="tx-hint">{w.event}</span></td>
      <td>{w.name || w.email}</td>
      <td className="tx-num">{money(w.bidCents)}</td>
      <td className="tx-num">{w.fmvCents == null ? <span className="tx-err">{t('tax.fmv.notSet', 'Not set')}</span> : money(w.fmvCents)}</td>
      <td><input type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} aria-label={t('tax.auctions.amount', 'Amount paid')} /></td>
      <td><input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label={t('tax.auctions.date', 'Date paid')} /></td>
      <td>
        <div className="lm-actions">
          <button type="button" className="lm-btn secondary" onClick={save}>{paid ? t('tax.save', 'Save') : t('tax.auctions.markPaid', 'Mark paid')}</button>
          {paid && <button type="button" className="lm-btn secondary" onClick={clear}>{t('tax.auctions.unpaid', 'Unpaid')}</button>}
        </div>
        {paid && <span className="tx-saved">{t('tax.auctions.paid', 'Paid')}</span>}
        {err && <span className="tx-err"> {err}</span>}
      </td>
    </tr>
  );
}

export default function TaxAuctions({ year }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try { setData(await listAuctionWinners(year)); } catch (err) { setError(errorText(err, t('tax.auctions.loadError', 'Could not load auction winners.'))); }
  }, [year, t]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="surface" aria-labelledby="tx-auc-title">
      <h4 id="tx-auc-title">{t('tax.auctions.title', 'Auction winners')}</h4>
      <p className="surface-muted perf-small">{t('tax.auctions.intro', 'Winning bids are pledges until paid. Record payments here; unpaid pledges are left off donors’ tax documents.')}</p>
      {error && <div className="note warn">{error}</div>}
      {!data && !error && <LoadingState />}
      {data && (data.winners.length === 0
        ? <p className="surface-muted perf-small">{t('tax.auctions.empty', 'No auction winners this year.')}</p>
        : (
          <div className="perf-table-wrap">
            <table className="perf-table tx-table">
              <thead><tr>
                <th>{t('tax.fmv.lot', 'Lot')}</th><th>{t('tax.auctions.winner', 'Winner')}</th><th className="tx-num">{t('tax.auctions.bid', 'Winning bid')}</th>
                <th className="tx-num">{t('tax.fmv.fmv', 'FMV')}</th><th>{t('tax.auctions.amount', 'Amount paid')}</th><th>{t('tax.auctions.date', 'Date paid')}</th><th />
              </tr></thead>
              <tbody>{data.winners.map((w) => <Row key={w.id} w={w} onChanged={load} t={t} />)}</tbody>
            </table>
          </div>
        ))}
    </section>
  );
}
